import * as React from 'react';
import { Alert, Col, Row } from 'reactstrap';
import { AccentDialog } from './AccentDialog';
import { AccentButton } from '../controls/AccentButton';
import { AccentSpinner } from '../controls/AccentSpinner';
import { AccentComboUnbound } from '../controls/unbound/AccentComboUnbound';
import { AccentTextUnbound } from '../controls/unbound/AccentTextUnBound';
import { AccentNumberUnbound } from '../controls/unbound/AccentNumberUnbound';
import { queryNoTracking, update } from '../services/DataService';
import { accentUtils, showOK } from '../services/HelperService';

const query_CostcoCashCard_SelectList = queryNoTracking("CostcoCashCard_SelectList");
const query_CostcoPaymentMethod_SelectList = queryNoTracking("CostcoPaymentMethod_SelectList");

const captureFieldLabels = {
    MembershipNumber: "Membership Number",
    CostcoCashCardID: "Cash Card Promotion",
    CostcoShopCardAmount: "Costco Shop Card",
    CostcoPaymentMethodID: "Payment Method",
    ExecutiveMember: "Executive Member",
    CostcoVisaCard: "Costco Visa Card"
};

// Tri-state yes/no so "not asked" (null) is distinguishable from an answered no.
const triStateOptions = [
    { ID: "true", DisplayName: "Yes" },
    { ID: "false", DisplayName: "No" }
];

const toTriState = (value) => value === true ? "true" : value === false ? "false" : null;
const fromTriState = (value) => value === "true" ? true : value === "false" ? false : null;

// Checkpoint shown when emailing/printing a quote for a Costco linked job. Captures the
// Costco lead data needed for the Quote milestone and lists anything that is still missing.
export const CostcoQuoteCheckpointDlg = ({ jobID, preflight, onClose }) => {
    const [loading, setLoading] = React.useState(true);
    const [cashCards, setCashCards] = React.useState([]);
    const [paymentMethods, setPaymentMethods] = React.useState([]);
    const [missing, setMissing] = React.useState(preflight.Missing ?? []);
    const [uncaptured, setUncaptured] = React.useState(preflight.Uncaptured ?? []);
    // Whether any values were written to the lead while the dialog was open. Reported on
    // every exit path (including cancel) so the caller knows the job view is now stale.
    const saved = React.useRef(false);

    const lead = preflight.Lead ?? {};
    const values = React.useRef({
        MembershipNumber: lead.MembershipNumber ?? null,
        CostcoCashCardID: lead.CostcoCashCardID ?? null,
        CostcoShopCardAmount: lead.CostcoShopCardAmount ?? null,
        CostcoPaymentMethodID: lead.CostcoPaymentMethodID ?? null,
        ExecutiveMember: lead.ExecutiveMember ?? null,
        CostcoVisaCard: lead.CostcoVisaCard ?? null
    });

    React.useEffect(() => {
        let isMounted = true;

        Promise.all([
            query_CostcoCashCard_SelectList.getAll({ includeID: lead.CostcoCashCardID }),
            query_CostcoPaymentMethod_SelectList.getAll({ includeID: lead.CostcoPaymentMethodID })
        ]).then(([cards, methods]) => {
            if (!isMounted) return;

            setCashCards(cards ?? []);
            setPaymentMethods(methods ?? []);
            setLoading(false);
        });

        return () => {
            isMounted = false;
        };
    }, []);

    const setValue = (name, value) => {
        values.current[name] = value;
    };

    const isUncaptured = (field) => uncaptured.includes(field);

    const notCapturedBadge = (field) => isUncaptured(field)
        ? <span className="text-danger" style={{ fontSize: "0.75rem" }}>Not captured</span>
        : null;

    const saveValues = async () => {
        const saveResult = await update("CostcoIntegration_SaveQuoteCheckpoint", {
            CostcoLeadID: preflight.CostcoLeadID,
            ...values.current
        }, true);

        if (!saveResult?.Success) {
            await showOK("Costco Quote Checkpoint", saveResult?.Message || "Unable to save the Costco lead.", ["application_strings.application.buttons.ok"], false, null, true);
            return false;
        }

        saved.current = true;
        return true;
    };

    const doSaveAndContinue = async (e) => {
        if (!await saveValues()) {
            e.promise();
            return;
        }

        const check = await update("CostcoIntegration_PreflightQuoteMilestone", { JobID: jobID }, true);

        if (check?.Success && check.Applicable && check.HasMissingData) {
            setMissing(check.Missing ?? []);
            setUncaptured(check.Uncaptured ?? []);
            e.promise();
            return;
        }

        onClose({ saved: saved.current });
        e.promise();
    };

    const doContinueAnyway = async (e) => {
        await saveValues();
        onClose({ saved: saved.current });
        e.promise();
    };

    // Cancel/X: an earlier "Save & Continue" may already have written values, so the
    // caller still needs to refresh the job even though the send is being abandoned.
    const doCancel = () => onClose({ canceled: true, saved: saved.current });

    const hasMissingData = missing.length > 0 || uncaptured.length > 0;
    const uncapturedLabels = uncaptured.map(f => captureFieldLabels[f] ?? f);

    const actions = [
        <AccentButton key="save" disabled={loading} onClick={doSaveAndContinue}>{hasMissingData ? "Save & Continue" : "Continue"}</AccentButton>
    ];

    if (hasMissingData && preflight.CanContinueWithMissing === true) {
        actions.unshift(<AccentButton key="continueAnyway" disabled={loading} onClick={doContinueAnyway}>Continue Anyway</AccentButton>);
    }

    return (
        <AccentDialog
            headerContent="Costco Quote Checkpoint"
            onClose={doCancel}
            maxWidth={800}
            actionsContent={actions}
        >
            {loading && <AccentSpinner />}
            {!loading && (
                <>
                    {hasMissingData && (
                        <Alert color="warning">
                            <div style={{ fontWeight: "bold" }}>The Costco lead is missing data needed for the Quote milestone.</div>
                            <ul className="mb-0 mt-1">
                                {missing.map((m, i) => <li key={`m${i}`}>{m}</li>)}
                                {uncapturedLabels.length > 0 && <li>{`Not captured yet: ${uncapturedLabels.join(", ")}.`}</li>}
                            </ul>
                        </Alert>
                    )}
                    {!hasMissingData && (
                        <Alert color="success" className="mb-2">
                            All Costco lead data for the Quote milestone has been captured. Review the values below before continuing.
                        </Alert>
                    )}
                    <Row>
                        <Col md={6}>
                            <AccentTextUnbound
                                label="Membership Number"
                                defaultValue={values.current.MembershipNumber}
                                onChange={v => setValue("MembershipNumber", v)} />
                            {notCapturedBadge("MembershipNumber")}
                        </Col>
                        <Col md={6}>
                            <AccentComboUnbound
                                label="Payment Method"
                                data={paymentMethods}
                                valueField="ID"
                                displayField="DisplayName"
                                defaultValue={values.current.CostcoPaymentMethodID}
                                onChange={v => setValue("CostcoPaymentMethodID", v)} />
                            {notCapturedBadge("CostcoPaymentMethodID")}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <AccentComboUnbound
                                label="Cash Card Promotion"
                                data={cashCards}
                                valueField="ID"
                                displayField="DisplayName"
                                defaultValue={values.current.CostcoCashCardID}
                                onChange={v => setValue("CostcoCashCardID", v)} />
                            {notCapturedBadge("CostcoCashCardID")}
                        </Col>
                        <Col md={6}>
                            <AccentNumberUnbound
                                label="Costco Shop Card"
                                defaultValue={values.current.CostcoShopCardAmount}
                                onChange={v => setValue("CostcoShopCardAmount", v)} />
                            {notCapturedBadge("CostcoShopCardAmount")}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <AccentComboUnbound
                                label="Executive Member"
                                data={triStateOptions}
                                valueField="ID"
                                displayField="DisplayName"
                                placeHolder="Not asked"
                                defaultValue={toTriState(values.current.ExecutiveMember)}
                                onChange={v => setValue("ExecutiveMember", fromTriState(v))} />
                            {notCapturedBadge("ExecutiveMember")}
                        </Col>
                        <Col md={6}>
                            <AccentComboUnbound
                                label="Costco Visa Card"
                                data={triStateOptions}
                                valueField="ID"
                                displayField="DisplayName"
                                placeHolder="Not asked"
                                defaultValue={toTriState(values.current.CostcoVisaCard)}
                                onChange={v => setValue("CostcoVisaCard", fromTriState(v))} />
                            {notCapturedBadge("CostcoVisaCard")}
                        </Col>
                    </Row>
                    {!accentUtils.isNull(preflight.IsUploaded) && !preflight.IsUploaded && missing.length === 0 && (
                        <Row>
                            <Col md={12}>
                                <label className="text-muted" style={{ fontSize: "0.8rem" }}>
                                    The Costco lead has not been uploaded to Centah yet. It will be uploaded by the lead sync, or can be uploaded manually from the Costco Lead section.
                                </label>
                            </Col>
                        </Row>
                    )}
                </>
            )}
        </AccentDialog>
    );
};
